import {
  computeWaterfall,
  getCoveredQuantums,
} from "../../../shared/apportionmentEngine";
import {
  ApportionedAmounts,
  ApportionmentContext,
} from "../../../shared/apportionmentTypes";
import { NamedEntity, OfferStatus } from "../../../shared/CaseModel";
import { settlementCaps } from "../../../shared/settlementCaps";
import { settlementCostsPaid } from "../../../shared/settlementCosts";
import type { Offer } from "./SettlementOfferDialog";

/** A claim between a party and one of its opponents, with what it claims. */
export interface PairClaim {
  id: string;
  /** The number the page of the claims gives it. */
  number: number;
  title: string;
  amounts: { id: string; title: string; value: number }[];
}

/**
 * The offers between a party and one of its opponents, in the order they were
 * made, and the claims between the two.
 */
export interface OpponentSettlements {
  opponent: NamedEntity;
  claims: PairClaim[];
  offers: Offer[];
}

/**
 * What an offer's sum can be paid towards, capped as the calculation caps it
 * (see settlementCaps): the costs of the side that receives it, less what its
 * earlier settlements used up, with the offer's own settlement costs as they
 * stand in it, and the damages of the claims it settles.
 */
export function apportionmentContextOf(offer: Offer): ApportionmentContext {
  const receiver = offer.payment.offereePays ? "offeror" : "offeree";
  const payer = receiver === "offeror" ? offer.offeree : offer.offeror;
  const { basis, vatRate } = offer.bases[receiver];
  const covered = getCoveredQuantums(offer.claims, offer.quantums);
  return {
    settlementAmount: offer.payment.amount,
    caps: {
      ...settlementCaps(
        basis,
        {
          offerId: offer.id,
          opponentId: payer.id,
          stage: offer.stage,
          order: offer.order,
        },
        settlementCostsPaid(offer.costs.settlement[receiver], vatRate),
      ),
      quantums: Object.fromEntries(covered.map((q) => [q.id, q.value])),
    },
  };
}

/** What an offer pays towards, by the order of its apportionment. */
export function apportionedBy(offer: Offer): ApportionedAmounts {
  return computeWaterfall(
    offer.apportionment ?? [],
    apportionmentContextOf(offer),
  );
}

/** Whether an offer leaves none of its sum without something it is paid towards. */
export function isFullyApportioned(amounts: ApportionedAmounts): boolean {
  return amounts.remaining <= 0 && amounts.total > 0;
}

/**
 * How much a status says about where the two parties stand: an accepted offer
 * settles the matter, one that is pending comes before one that was rejected,
 * and a draft has not left the party yet.
 */
const WEIGHT: { [status in OfferStatus]: number } = {
  [OfferStatus.Accepted]: 3,
  [OfferStatus.Offered]: 2,
  [OfferStatus.Rejected]: 1,
  [OfferStatus.Draft]: 0,
};

const settles = (offer: Offer, claimId: string) =>
  offer.claims.some((claim) => claim.id === claimId && claim.settledByOffer);

/**
 * The offer that says where the two parties stand, on one claim or on all of
 * them: the one whose status weighs most, and of two alike the later one.
 */
export function decisiveOffer(
  offers: readonly Offer[],
  claimId?: string,
): Offer | undefined {
  let decisive: Offer | undefined;
  for (const offer of offers) {
    if (claimId !== undefined && !settles(offer, claimId)) continue;
    if (!decisive || WEIGHT[offer.status] >= WEIGHT[decisive.status])
      decisive = offer;
  }
  return decisive;
}

/** How many of the claims between the two an accepted offer settles. */
export function settledClaims({ claims, offers }: OpponentSettlements): number {
  return claims.filter(
    (claim) => decisiveOffer(offers, claim.id)?.status === OfferStatus.Accepted,
  ).length;
}

/**
 * Whether the two parties have settled: every claim between them is settled
 * by an accepted offer, so that there is nothing left to make an offer for.
 */
export function isSettled(pair: OpponentSettlements): boolean {
  return pair.claims.length > 0 && settledClaims(pair) === pair.claims.length;
}

/** How far a claimed amount is settled, and what of it is on offer. */
export interface AmountProgress {
  id: string;
  title: string;
  claimed: number;
  /** What accepted offers pay towards it. */
  settled: number;
  /** What offers that are made and not answered would pay towards it. */
  offered: number;
  /** The same per opponent, for the opponents that pay or are offered to pay. */
  parts: { opponent: NamedEntity; accepted: boolean; amount: number }[];
}

/** Where a claim of a party stands with each opponent it is brought against. */
export interface ClaimProgress {
  id: string;
  number: number;
  title: string;
  opponents: { opponent: NamedEntity; status?: OfferStatus }[];
  amounts: AmountProgress[];
}

/**
 * The claims of a party across its opponents: with whom each is settled or on
 * offer, and what that pays towards each of its claimed amounts.
 */
export function claimProgress(
  pairs: readonly OpponentSettlements[],
): ClaimProgress[] {
  const progress = new Map<string, ClaimProgress>();
  for (const { opponent, claims, offers } of pairs) {
    for (const claim of claims) {
      const entry: ClaimProgress = progress.get(claim.id) ?? {
        id: claim.id,
        number: claim.number,
        title: claim.title,
        opponents: [],
        amounts: claim.amounts.map(({ id, title, value }) => ({
          id,
          title,
          claimed: value,
          settled: 0,
          offered: 0,
          parts: [],
        })),
      };
      progress.set(claim.id, entry);

      const decisive = decisiveOffer(offers, claim.id);
      entry.opponents.push({ opponent, status: decisive?.status });
      if (
        !decisive ||
        (decisive.status !== OfferStatus.Accepted &&
          decisive.status !== OfferStatus.Offered)
      )
        continue;

      const accepted = decisive.status === OfferStatus.Accepted;
      const paid = apportionedBy(decisive).quantums;
      for (const amount of entry.amounts) {
        const towards = paid[amount.id] ?? 0;
        if (towards <= 0) continue;
        if (accepted) amount.settled += towards;
        else amount.offered += towards;
        amount.parts.push({ opponent, accepted, amount: towards });
      }
    }
  }
  return [...progress.values()].sort((a, b) => a.number - b.number);
}

/** How many offers of each status a party has made and received. */
export function offerCounts(pairs: readonly OpponentSettlements[]): {
  total: number;
  pending: number;
  accepted: number;
  rejected: number;
  drafts: number;
} {
  const offers = pairs.flatMap((pair) => pair.offers);
  const count = (status: OfferStatus) =>
    offers.filter((offer) => offer.status === status).length;
  return {
    total: offers.length,
    pending: count(OfferStatus.Offered),
    accepted: count(OfferStatus.Accepted),
    rejected: count(OfferStatus.Rejected),
    drafts: count(OfferStatus.Draft),
  };
}

/**
 * The amount of an offer as the party sees it: above zero where it is paid to
 * the party, below zero where the party pays it. The bargaining range of the
 * party against its opponent is in the same terms.
 */
export function signedAmount(offer: Offer, partyId: string): number {
  const payerId = offer.payment.offereePays
    ? offer.offeree.id
    : offer.offeror.id;
  return payerId === partyId ? -offer.payment.amount : offer.payment.amount;
}
